/**
 * Created by rathompson on 5/3/22
 * File: AssociateApp
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useMemo, useState } from 'react'
import { SheetBody, Body, Button, noop, LoadingIcon, Bold, useModal } from '@panwds/react-ui'
import { Form, SubmitButton, RadioGroup } from '@panwds/react-form'
import { useFormContext } from 'react-hook-form'
import { BUTTON_CANCEL, BUTTON_SAVE, BUTTON_REMOVE, LABEL_REMOVE_APPS } from '../../constants/LabelConstants'
import AppContext from '../../../common/context/AppContext'
import SubscriptionService from '../../../subscription/services/SubscriptionService'
import styled from 'styled-components'
// import PropTypes from 'prop-types'
import RemoveAppsModal from '../modal/RemoveAppsModal'
import { ARRAY, getDasAppDisplay } from '../../../common/utils/Utils'
import { DEVICE_STATUSES } from '../../constants/Constants'

const CONTAINER = styled.div`
  align-items: baseline;
  input[type=checkbox] {
    min-height: 1rem;
    min-width: 1rem;
    font-family: inherit !important;
    position: initial !important;
    -moz-appearance: auto !important;
    -webkit-appearance: checkbox !important;
    -o-appearance: auto !important;

    &[disabled] {
      background-image: none;
      border-width: 1px;
      border-style: solid;
      border-radius: 0.063rem;
      --tw-border-opacity: 1;
      border-color: rgba(218,219,219,var(--tw-border-opacity));
      background-color: transparent;
    }
  }
`

const Footer = ({ onSidecarClose, action }) => {
  const { formState, getValues } = useFormContext()
  const { isValid } = formState

  const values = getValues()

  const disabled = useMemo(() => {
    return !isValid || Object.values(values).every(value => !Boolean(value))
  }, [isValid, values])

  const btnText = useMemo(() => (action === 'remove' ? BUTTON_REMOVE : BUTTON_SAVE), [action])

  return (
    <div className='tw-flex tw-gap-2 tw-justify-end'>
      <Button appearance='secondary' onClick={onSidecarClose}>{BUTTON_CANCEL}</Button>
      <SubmitButton disabled={disabled} appearance='primary'>{btnText}</SubmitButton>
    </div>
  )
}

const AppCheckBox = ({ ...instance }) => {
  const { disabled, disableReason } = instance
  const { apps, getAppName } = useContext(AppContext)
  const { appName, sub_title } = getDasAppDisplay(instance, apps, getAppName)
  return (
    <CONTAINER className='tw-flex tw-w-full tw-gap-2 tw-p-3'>
      <div className='tw-flex tw-flex-col'>
        <Body size='lg' addClassName={disabled ? 'tw-text-disabled' : ''}>{appName}</Body>
        {sub_title && <Body addClassName='tw-text-secondary'>{sub_title}</Body>}
        { disableReason && <Body addClassName='tw-text-secondary'>{disableReason}</Body>}
      </div>
    </CONTAINER>
  )
}

const AssociateApps = ({
  onSidecarClose = noop,
  sidecar = {}
}) => {

  const removeModal = useModal(RemoveAppsModal)
  const { dontRenderApp, apps } = useContext(AppContext)
  const appsMap = useMemo(() => new Map(Object.values(apps)?.map(app => [app.app_id, app])), [apps])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { tenant = {}, action = '', devices = [], instances = [], callback = () => {}, fetchDevices = async () => {} } = sidecar?.extra || {}

  const deviceApps = useMemo(() => {
    const apps = []
    for (const device of devices) {
      apps.push(...device.associations.map(({ app_id }) => app_id))
    }
    return [...new Set(apps)].map(app_id => instances.find(instance => instance.app_id === app_id))
  }, [devices, instances])

  const associations = useMemo(() => {
    let apps = ARRAY.clean(action === 'add' ? instances : action === 'remove' ? deviceApps : [])
    apps = apps?.filter(app => !dontRenderApp(app?.app_id))
    for (const app of apps) {
      if (action === 'add') {
        app.disabled = Boolean(devices.every(device => Boolean(device.associations.find(({ app_id }) => app_id === app.app_id))))
      }
      else if (action === 'remove' && !appsMap?.get(app.app_id)?.device_association?.remove_association) {
        app.disabled = true
        app.disableReason = 'Remove association is not supported'
      }
      else {
        const deviceAssociations = devices.reduce((array, device) => {
          array.push(...device.associations.filter(instance => instance.app_id === app.app_id))
          return array
        }, [])
        if (deviceAssociations.every(instance => ![DEVICE_STATUSES.COMPLETED, DEVICE_STATUSES.FAILED].includes(instance.association_status))) {
          app.disabled = true
          app.disableReason = 'Device association is not finished, hence remove association is not allowed.'
        }
      }
    }
    return apps
  }, [action, instances, deviceApps, dontRenderApp, appsMap, devices])

  const appsRadioComponent = useMemo(() => associations.map((association) => {
    const { tenant_id, disabled } = association
    return { value: tenant_id, disabled, children: <AppCheckBox {...association }/> }
  }), [associations])

  const handleAddSubmit = useCallback(async ({ tenant_id }) => {
    try {
      setLoading(true)
      const associations = []
      for (const { serial_number } of devices) {
        associations.push({
          serial_number,
          tenant_id
        })
      }
      await SubscriptionService.devices.associateApps({
        tsg_id: tenant?.id,
        associations
      })
      callback({ devices, tenant })
      onSidecarClose()
    }
    catch (error) {
      setError(error.message)
    }
    setLoading(false)
  }, [devices, callback, tenant, onSidecarClose])

  const handleRemoveSubmit = useCallback(async ({ tenant_id }) => {
    try {
      setLoading(true)
      const array = []
      const deviceMap = {}
      for (const device of devices) {
        const { serial_number, associations } = device
        const associate = associations.find(instance => instance.tenant_id === tenant_id)
        if (!associate || ![DEVICE_STATUSES.COMPLETED, DEVICE_STATUSES.FAILED].includes(associate?.association_status)) {
          continue
        }
        if (!deviceMap.hasOwnProperty(serial_number)) {
          deviceMap[serial_number] = device
        }
        array.push({
          serial_number,
          tenant_id
        })
      }
      await SubscriptionService.devices.dissociateApps({
        tsg_id: tenant?.id,
        associations: array
      })
      await fetchDevices()
      callback({ devices: Object.values(deviceMap), apps: [tenant_id] })
      onSidecarClose()
    }
    catch (error) {
      setError(error.message)
    }
    setLoading(false)
  }, [callback, fetchDevices, devices, onSidecarClose, tenant?.id])

  const onRemoveClick = useCallback((formData) => {
    const callback = () => handleRemoveSubmit(formData)
    const appId = associations?.find(e => e.tenant_id === formData.tenant_id)?.app_id
    const warningMessage = appsMap?.get(appId)?.device_association?.app_override?.remove_warning_message || LABEL_REMOVE_APPS
    removeModal.openModal({ callback, warningMessage })
  }, [appsMap, associations, handleRemoveSubmit, removeModal])

  const onSubmit = useCallback(async (formData) => {
    if (action === 'add') {
      await handleAddSubmit(formData)
    }
    else if (action === 'remove') {
      await onRemoveClick(formData)
    }
  }, [action, handleAddSubmit, onRemoveClick])

  return (
    <SheetBody>
      <div className='tw-relative'>
        {loading && (
          <div className='tw-flex tw-justify-center tw-items-center tw-absolute tw-inset-0 tw-bg-white dark:tw-bg-gray-1050' style={{ zIndex: 1052 }}>
            <LoadingIcon size='4xl'/>
          </div>
        )}
        <Form mode='onChange' onSubmit={onSubmit}>
          {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
          <RadioGroup name='tenant_id' label='apps' showLabel={false} items={appsRadioComponent}/>
          <hr/>
          <Footer {...{ onSidecarClose, action }} />
        </Form>
      </div>
    </SheetBody>
  )
}

AssociateApps.propTypes = {}
export default AssociateApps
